import { useCallback } from 'react';

import { FileContext } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';

import {
  useUploadFileV2Mutation,
  useUploadPlatformFileMutation,
} from '../../graphql/FileUploaderV2.generated';
import {
  FileUploadError,
  FileUploadErrorCode,
  getSha256HashFromArrayBuffer,
} from '../FileUploader.utils';
import { useFakeUploadProgress } from './useFakeUploadProgress';
import { UploadedFileData } from './useUploadFile';

export interface UseUploadFileV2Props<_Mutation> {
  // When uploading a single file, we can set incremental progress values.
  setSingleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  // When uploading multiple files, we can increment the progress value.
  setMultipleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  // File context for categorizing uploads
  context?: FileContext;
  // Household ID for HOUSEHOLD context files
  householdId?: string | null;
}

export function useUploadFileV2<Mutation>({
  setSingleFileUploadProgress,
  setMultipleFileUploadProgress,
  context = FileContext.Household,
  householdId,
}: UseUploadFileV2Props<Mutation>) {
  const [uploadFileV2Mutation] = useUploadFileV2Mutation();
  const [uploadPlatformFileMutation] = useUploadPlatformFileMutation();
  const { start: startFakeProgress, stop: stopFakeProgress } =
    useFakeUploadProgress(setSingleFileUploadProgress);

  const logUploadError = useCallback(
    (error: Error, file: File) => {
      diagnostics.error('File upload failed', error, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        householdId,
        context,
      });
    },
    [householdId, context]
  );

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedFileData<Mutation>> => {
      if (file.size === 0) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(FileUploadErrorCode.EmptyFile);
        throw error;
      }

      startFakeProgress();

      // Read the file into memory once upfront. Files from network drives or
      // cloud-synced folders can become unavailable between reads, so we capture
      // the bytes eagerly and derive both the hash and the upload payload from
      // the same buffer.
      let hash: string;
      let fileBlob: File;
      try {
        const arrayBuffer = await file.arrayBuffer();
        hash = await getSha256HashFromArrayBuffer(arrayBuffer);
        // Construct a new File from the in-memory buffer so apollo-upload-client
        // doesn't need to re-read the original (potentially unavailable) File.
        fileBlob = new File([arrayBuffer], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      } catch (err) {
        stopFakeProgress();
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.HashFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }

      try {
        const uploadMutation =
          context === FileContext.Platform
            ? uploadPlatformFileMutation
            : uploadFileV2Mutation;
        const result = await uploadMutation({
          variables: {
            input: {
              contentsSha256: hash,
              context,
              householdID:
                context === FileContext.Household ? householdId : undefined,
            },
            upload: fileBlob,
          },
          context: {
            hasUpload: true,
          },
        });

        stopFakeProgress();
        setSingleFileUploadProgress(100);
        setMultipleFileUploadProgress((lastValue) => lastValue + 1);

        return { file, result } as UploadedFileData<Mutation>;
      } catch (err) {
        stopFakeProgress();
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.UploadFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }
    },
    [
      uploadFileV2Mutation,
      uploadPlatformFileMutation,
      startFakeProgress,
      stopFakeProgress,
      setSingleFileUploadProgress,
      setMultipleFileUploadProgress,
      context,
      householdId,
      logUploadError,
    ]
  );

  return {
    uploadFile,
  };
}
